import {Camera} from '@mediapipe/camera_utils';
import {Hands} from '@mediapipe/hands';
import {Pose} from '@mediapipe/pose';

import {ETextParamType_StopWebCamTracking, ETextParamType_OperateCanvas} from './logics/const';
import {hel_webcamtracking_sethanddata, hel_webcamtracking_setposedata} from './logics/misc';
import {hel_setText} from './logics/moduleCaller';
import {hel_isMobile} from './logics/platform';
import {hel_onloaded_add} from './logics/shared';

import type {Landmark} from '@mediapipe/pose';

//
let g_WebCamTrackingOnLoaded: boolean = false;

let g_WebCamTrackingScriptOnLoaded: boolean = false;

// 全身トラッキング
let LeftWristPos: Landmark | null = null;
let RightWristPos: Landmark | null = null;
let pose: Pose | null = null;
let hands: Hands | null = null;

// WebCam
let g_WebCamTrackingRun = false;

let camera: Camera | null = null;

let g_webcam_videoElement: HTMLVideoElement | null = null;

const createAPIScripts = async () => {
  g_WebCamTrackingScriptOnLoaded = true;

  // トラッキング開始
  startWebCamTracking();
};

const removeScriptTag = (src: string) => {
  const scriptTagList = document.querySelectorAll(`script[src="${src}"]`);

  for (let i = 0; i < scriptTagList.length; i++) {
    const scriptTag = scriptTagList[i];
    if (scriptTag) {
      scriptTag.remove();
    }
  }
};

const OnLoadedForWebCamTracking = () => {
  g_WebCamTrackingOnLoaded = true;
};

hel_onloaded_add(OnLoadedForWebCamTracking);

const webcamtracking_pose_start = () => {
  if (pose !== null) return;

  pose = new Pose({
    locateFile: (file) => {
      return `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`;
    }
  });

  pose.setOptions({
    modelComplexity: 1,
    smoothLandmarks: true,
    enableSegmentation: true,
    smoothSegmentation: true,
    minDetectionConfidence: 0.5,
    minTrackingConfidence: 0.5
  });

  pose.onResults((results) => {
    // console.debug(results);

    if (!g_WebCamTrackingOnLoaded) return;

    const poseLandmarks = results.poseLandmarks;
    const poseWorldLandmarks = results.poseWorldLandmarks;

    if (
      poseLandmarks === undefined ||
      poseLandmarks.length !== 33 ||
      poseWorldLandmarks === undefined ||
      poseWorldLandmarks.length !== 33
    ) {
      return;
    }

    let Text = '0'; // バージョン

    // ３Ｄ座標系
    for (const [n, Landmark] of poseWorldLandmarks.entries()) {
      const X = -Landmark.x;
      const Y = -Landmark.y + 0.75;
      const Z = -Landmark.z;
      const V = Landmark.visibility;

      if (n === 15) {
        LeftWristPos = Landmark;
        LeftWristPos.x = X;
        LeftWristPos.y = Y;
        LeftWristPos.z = Z;
      } else if (n === 16) {
        RightWristPos = Landmark;
        RightWristPos.x = X;
        RightWristPos.y = Y;
        RightWristPos.z = Z;
      }

      Text += ',' + String(X) + ',' + String(Y) + ',' + String(Z) + ',' + String(V);
    }

    // スクリーン座標系
    for (const Landmark of poseLandmarks) {
      const X = Landmark.x;
      const Y = Landmark.y;
      const Z = Landmark.z;
      const V = Landmark.visibility;

      Text += ',' + String(X) + ',' + String(Y) + ',' + String(Z) + ',' + String(V);
    }

    //
    hel_webcamtracking_setposedata(Text);
  });
};

const webcamtracking_pose_stop = () => {
  if (pose === null) return;

  pose.close();

  pose = null;

  // APIにより自動生成されるscriptタグが残り続けるので明示的に削除する
  removeScriptTag(
    'https://cdn.jsdelivr.net/npm/@mediapipe/pose/pose_solution_packed_assets_loader.js'
  );
  removeScriptTag('https://cdn.jsdelivr.net/npm/@mediapipe/pose/pose_solution_simd_wasm_bin.js');
};

// ハンドトラッキング
const onHandsResults = (results: any) => {
  // console.debug(results);

  const keypointsWorldHand = results.multiHandWorldLandmarks;
  const keypointsHand = results.multiHandLandmarks;
  const multiHandedness = results.multiHandedness;

  if (
    keypointsWorldHand === null ||
    keypointsWorldHand.length <= 0 ||
    keypointsHand === null ||
    keypointsHand.length <= 0 ||
    !g_WebCamTrackingOnLoaded
  ) {
    return;
  }

  let Text = '0'; // バージョン

  // ワールド座標
  Text += ',' + String(keypointsWorldHand.length); // 個数

  for (let n = 0; n < keypointsWorldHand.length; n++) {
    const Left = !(multiHandedness[n].label === 'Left');

    Text += ',' + (Left ? '0' : '1'); // 左右フラグ

    const PosList = keypointsWorldHand[n];

    const WristX = PosList[0].x;
    const WristY = PosList[0].y;
    const WristZ = PosList[0].z;

    for (let m = 0; m < PosList.length; m++) {
      const Pos = PosList[m];

      let X = Pos.x;
      let Y = Pos.y;
      let Z = Pos.z;

      X -= WristX;
      Y -= WristY;
      Z -= WristZ;

      X = -X;
      Y = -Y;
      Z = -Z;

      if (Left && LeftWristPos != null) {
        X = X + LeftWristPos.x;
        Y = Y + LeftWristPos.y;
        Z = Z + LeftWristPos.z;
      } else if (!Left && RightWristPos != null) {
        X = X + RightWristPos.x;
        Y = Y + RightWristPos.y;
        Z = Z + RightWristPos.z;
      } else {
        X = X + WristX;
        Y = Y + WristY;
        Z = Z + WristZ;
      }

      Text += ',' + String(X) + ',' + String(Y) + ',' + String(Z);
    }
  }

  // スクリーン座標
  Text += ',' + String(keypointsHand.length); // 個数

  for (let n = 0; n < keypointsHand.length; n++) {
    const Left = !(multiHandedness[n].label === 'Left');

    Text += ',' + (Left ? '0' : '1'); // 左右フラグ

    const PosList = keypointsHand[n];

    for (let m = 0; m < PosList.length; m++) {
      const Pos = PosList[m];

      const X = Pos.x;
      const Y = Pos.y;
      const Z = Pos.z;

      Text += ',' + String(X) + ',' + String(Y) + ',' + String(Z);
    }
  }

  //
  hel_webcamtracking_sethanddata(Text);
};

const webcamtracking_hands_start = () => {
  if (hands !== null) return;

  hands = new Hands({
    locateFile: (file) => {
      return `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`;
    }
  });

  hands.setOptions({
    selfieMode: false,
    maxNumHands: 2,
    modelComplexity: 1,
    minDetectionConfidence: 0.5,
    minTrackingConfidence: 0.5
  });

  hands.onResults(onHandsResults);
};

const webcamtracking_hands_stop = () => {
  if (hands === null) return;

  hands.close();

  hands = null;

  // APIにより自動生成されるscriptタグが残り続けるので明示的に削除する
  removeScriptTag(
    'https://cdn.jsdelivr.net/npm/@mediapipe/hands/hands_solution_packed_assets_loader.js'
  );
  removeScriptTag('https://cdn.jsdelivr.net/npm/@mediapipe/hands/hands_solution_simd_wasm_bin.js');
};

const startCamera = async () => {
  try {
    if (camera === null) {
      const videoElement = g_webcam_videoElement;
      if (!videoElement) {
        throw new TypeError('Expected g_webcam_videoElement to be a non-null HTMLVideoElement');
      }
      camera = new Camera(videoElement, {
        onFrame: async () => {
          if (pose !== null) {
            await pose.send({image: videoElement});
          }

          if (hands !== null) {
            await hands.send({image: videoElement});
          }
        },
        width: 1280,
        height: 720
      });
    }

    // カメラ開始
    await camera.start();
  } catch (error) {
    console.error(error);

    // カメラが使えないのでモーションキャプチャを終了する
    hel_setText(ETextParamType_StopWebCamTracking, '');
    hel_setText(
      ETextParamType_OperateCanvas,
      'ToggleDefault,,SettingSystem_WebCamTracking_Enable_Disable'
    );
    hel_setText(
      ETextParamType_OperateCanvas,
      'ToggleDefaultChange,,SettingSystem_WebCamTracking_Available'
    );
  }
};

const stopCamera = async () => {
  if (camera === null) return;

  // カメラ停止
  await camera.stop();
};

const createCameraStream = async () => {
  if (g_webcam_videoElement !== null && g_webcam_videoElement.srcObject === null) {
    // スマホではstreamを渡す必要がある
    const constraints = {
      video: {
        facingMode: 'user' // 内カメラを指定するためのオプション
      }
    };

    const webcam_stream = await window.navigator.mediaDevices.getUserMedia(constraints);

    g_webcam_videoElement.srcObject = webcam_stream;
  }

  await startCamera();
};

const startWebCamTracking = () => {
  webcamtracking_pose_start();
  webcamtracking_hands_start();

  // Videoを動的に生成
  if (g_webcam_videoElement === null) {
    // bodyに追加しないので画面には表示されない
    g_webcam_videoElement = document.createElement('video');

    g_webcam_videoElement.className = 'webcam_video';

    // 念のためCSSでも非表示状態にする
    // サイズを0にして見えなくする(カメラには1280x720が指定されているので認識はされる)
    g_webcam_videoElement.width = 0;
    g_webcam_videoElement.height = 0;
    g_webcam_videoElement.style.visibility = 'hidden';
  }

  if (hel_isMobile()) {
    // スマホの時は先にstreamを作成する
    createCameraStream();
  } else {
    startCamera();
  }

  g_WebCamTrackingRun = true;
};

// 試験的にCPU/GPUモードや推論モデルの切り替えができるheliodor_core_webcamtracking_advancedの方を使うのでコメントアウトしておく
/*
Object.assign(globalThis, {
  hel_webcamtracking_start: () => {
    if (hel_setText(ETextParamType_IsAvailableWebCamTracking, '') == 0) return;

    // スマホでは重いので非対応とする
    if (hel_isMobile()) return;

    if (g_WebCamTrackingRun) return;

    if (!g_WebCamTrackingScriptOnLoaded) {
      // scriptタグを作成
      createAPIScripts();
    } else {
      startWebCamTracking();
    }
  },

  hel_webcamtracking_stop: () => {
    if (hel_setText(ETextParamType_IsAvailableWebCamTracking, '') == 0) return;

    // スマホでは重いので非対応とする
    if (hel_isMobile()) return;

    if (!g_WebCamTrackingRun) return;

    // Cameraとhands/poseを両方とも停止するとmediaposeのwasm内でエラーが出る
    // Camera.stopを呼べばonPoseResultsやOnHandsResultsのコールバックは呼ばれなくなるので実質停止扱いになる
    // なので一度MediaPipeのリソースを作成したら、UIのEnable/DisableではCameraのstart/stopを切り替えるだけにする
    stopCamera();
    // webcamtracking_hands_stop();
    // webcamtracking_pose_stop();

    g_WebCamTrackingRun = false;
  }
});
*/
